// Gives every kit its own waveform colours: a two-tone gradient that runs
// left to right across the loop.
//
// Picked for the site, not copied from the plugin: every pair is saturated
// but light enough to glow on #050505, each pair shares one end with the
// next so the set reads as one family, and the family is built outward from
// the brand orange (ember). Solid single colours looked flat in a list, and
// the plugin's own white-mixed lane tints turn chalky on near-black.
//
// The pick is a hash of the kit id, so a kit keeps the same colours on every
// visit and on every visitor's screen, with nothing stored anywhere.

export const PALETTE = [
  { from: [255, 176, 56], to: [255, 92, 58] }, // sunrise: amber to ember
  { from: [255, 104, 64], to: [255, 70, 140] }, // ember to rose
  { from: [255, 84, 150], to: [168, 104, 255] }, // rose to violet
  { from: [176, 108, 255], to: [84, 140, 255] }, // violet to azure
  { from: [80, 136, 255], to: [46, 212, 240] }, // azure to cyan
  { from: [44, 208, 236], to: [58, 228, 160] }, // cyan to mint
  { from: [56, 226, 168], to: [176, 234, 86] }, // mint to lime
  { from: [214, 98, 255], to: [255, 118, 84] }, // orchid to ember
];

// FNV-1a, 32-bit. Tiny, deterministic, and spreads short ids well.
function hashString(s) {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function paletteIndexFor(id) {
  return hashString(String(id ?? "")) % PALETTE.length;
}

// Returns { from, to }, each [r, g, b], so callers apply their own alpha.
export function kitWaveColor(id) {
  return PALETTE[paletteIndexFor(id)];
}

// Colours for a list of rows, in display order. Each kit starts from its own
// hashed colour, but a row never repeats either of the two rows above it,
// and never sits next to a palette neighbour of the row just above (azure
// under violet reads as "the same blue"): with ten colours a straight hash
// puts near-twins side by side far too often. A clash steps round the
// palette by 3, which lands on a clearly different hue. So a kit keeps its
// colour unless a neighbour already has it or something close to it.
export function assignRowColors(ids) {
  const n = PALETTE.length;
  const out = [];
  for (let i = 0; i < ids.length; i += 1) {
    const taken = new Set(out.slice(Math.max(0, i - 2)));
    if (i > 0) {
      const prev = out[i - 1];
      taken.add((prev + 1) % n);
      taken.add((prev + n - 1) % n);
    }
    let idx = paletteIndexFor(ids[i]);
    for (let tries = 0; taken.has(idx) && tries < PALETTE.length; tries += 1) {
      idx = (idx + 3) % PALETTE.length;
    }
    out.push(idx);
  }
  return out.map((idx) => PALETTE[idx]);
}

// Halfway between the two ends: the glow colour.
export function midColor({ from, to }) {
  return from.map((c, i) => Math.round((c + to[i]) / 2));
}

// Mix towards white by `amount` (0..1).
export function tint([r, g, b], amount) {
  return [r, g, b].map((c) => Math.round(c + (255 - c) * amount));
}

export function rgba([r, g, b], alpha) {
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
